import { useMemo } from "react";

import { EntityType } from "types/search";

import * as sections from "./sections";
import { useSearchSection } from "./useSearchSection";

const useSearchSections = (pattern: string, enables: EntityType[]) => {
	const {
		isFetching: isApplicationFetching,
		data: applicationData,
		SectionComponent: ApplicationSection,
	} = useSearchSection(
		sections.applicationSection(),
		pattern,
		enables.includes("application"),
	);

	const {
		isFetching: isMerchantFetching,
		data: merchantData,
		SectionComponent: MerchantSection,
	} = useSearchSection(
		sections.merchantSection(),
		pattern,
		enables.includes("merchant"),
	);

	const {
		isFetching: isUnderwritingFetching,
		data: underwritingData,
		SectionComponent: UnderwritingSection,
	} = useSearchSection(
		sections.underwritingSection(),
		pattern,
		enables.includes("uw"),
	);

	const {
		isFetching: isTerminalsFetching,
		data: terminalData,
		SectionComponent: TerminalsSection,
	} = useSearchSection(
		sections.terminalSection(),
		pattern,
		enables.includes("terminal"),
	);

	const {
		isFetching: isTidEquipmentFetching,
		data: tidEquipmentData,
		SectionComponent: TidEquipmentSection,
	} = useSearchSection(
		sections.tidEquipmentSection(),
		pattern,
		enables.includes("tid"),
	);
	const {
		isFetching: isInstallationFetching,
		data: installationData,
		SectionComponent: InstallationSection,
	} = useSearchSection(
		sections.installationSection(),
		pattern,
		enables.includes("installation"),
	);
	const {
		isFetching: isUserFetching,
		data: userData,
		SectionComponent: UserSection,
	} = useSearchSection(
		sections.userSection(),
		pattern,
		enables.includes("user"),
	);

	const {
		isFetching: isAgentFetching,
		data: agentData,
		SectionComponent: AgentSection,
	} = useSearchSection(
		sections.agentSection(),
		pattern,
		enables.includes("agent"),
	);
	const {
		isFetching: isAgentOfficeFetching,
		data: agentOfficeData,
		SectionComponent: AgentOfficeSection,
	} = useSearchSection(
		sections.agentOfficeSection(),
		pattern,
		enables.includes("agentOffice"),
	);

	const {
		isFetching: isPendFetching,
		data: pendData,
		SectionComponent: PendSection,
	} = useSearchSection(
		sections.pendSection(),
		pattern,
		enables.includes("pends"),
	);

	const {
		isFetching: isBoardingFetching,
		data: boardingData,
		SectionComponent: BoardingSection,
	} = useSearchSection(
		sections.boardingSection(),
		pattern,
		enables.includes("boarding"),
	);

	const searching = useMemo(
		() =>
			isApplicationFetching ||
			isMerchantFetching ||
			isUnderwritingFetching ||
			isTerminalsFetching ||
			isTidEquipmentFetching ||
			isInstallationFetching ||
			isUserFetching ||
			isAgentFetching ||
			isAgentOfficeFetching ||
			isPendFetching ||
			isBoardingFetching,
		[
			isAgentFetching,
			isAgentOfficeFetching,
			isApplicationFetching,
			isBoardingFetching,
			isInstallationFetching,
			isMerchantFetching,
			isPendFetching,
			isTerminalsFetching,
			isTidEquipmentFetching,
			isUnderwritingFetching,
			isUserFetching,
		],
	);

	const isResultsFound = useMemo(
		() =>
			applicationData?.count > 0 ||
			merchantData?.count > 0 ||
			underwritingData?.count > 0 ||
			terminalData?.count > 0 ||
			tidEquipmentData?.count > 0 ||
			installationData?.count > 0 ||
			userData?.count > 0 ||
			agentData?.count > 0 ||
			agentOfficeData?.count > 0 ||
			pendData?.count > 0 ||
			boardingData?.count > 0,
		[
			agentData?.count,
			agentOfficeData?.count,
			applicationData?.count,
			installationData?.count,
			merchantData?.count,
			terminalData?.count,
			tidEquipmentData?.count,
			underwritingData?.count,
			userData?.count,
			pendData?.count,
			boardingData?.count,
		],
	);

	return {
		searching,
		isResultsFound,
		ApplicationSection,
		MerchantSection,
		UnderwritingSection,
		TerminalsSection,
		TidEquipmentSection,
		InstallationSection,
		UserSection,
		AgentSection,
		AgentOfficeSection,
		PendSection,
		BoardingSection,
	};
};

export { useSearchSections };
